import { useState, useRef } from 'react'
import { api } from '../services/api.js'

const UNITS = ['L', 'mL', 'g', 'kg', 't']

// areas: AreaDetail[] (com area.ha)
// activities: ActivityConfigOut[] (habilitadas)
// fazendaId: number
// onClose(): fecha modal
// onApplied(): callback para refetch após aplicar
export default function RecommendationModal({ areas, activities, fazendaId, onClose, onApplied }) {
  const [selectedActivity, setSelectedActivity] = useState(activities[0]?.key ?? '')
  const [products, setProducts] = useState([])
  const [newName, setNewName] = useState('')
  const [newDose, setNewDose] = useState('')
  const [newUnit, setNewUnit] = useState('L')
  const [selectedAreaIds, setSelectedAreaIds] = useState([])
  const [applying, setApplying] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const nameRef = useRef()

  // ── Totais em tempo real ──────────────────────────────────────────────────
  const selectedHa = areas
    .filter(a => selectedAreaIds.includes(a.id))
    .reduce((s, a) => s + a.ha, 0)

  const totals = {}
  for (const p of products) {
    if (p.dose != null) {
      totals[p.product_name] = { total: +(p.dose * selectedHa).toFixed(2), unit: p.unit }
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  function addProduct() {
    const name = newName.trim()
    if (!name || products.some(p => p.product_name === name)) return
    setProducts(prev => [...prev, {
      product_name: name,
      dose: newDose !== '' ? parseFloat(newDose) : null,
      unit: newDose !== '' ? newUnit : null,
    }])
    setNewName('')
    setNewDose('')
    nameRef.current?.focus()
  }

  function removeProduct(name) {
    setProducts(prev => prev.filter(p => p.product_name !== name))
  }

  function toggleArea(id) {
    setSelectedAreaIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  function selectAll() { setSelectedAreaIds(areas.map(a => a.id)) }
  function clearAll() { setSelectedAreaIds([]) }

  async function apply() {
    if (!selectedActivity || products.length === 0 || selectedAreaIds.length === 0) return
    setApplying(true)
    setError('')
    try {
      const res = await api.bulkSetProducts(selectedActivity, products, selectedAreaIds, fazendaId)
      setResult(res)
      onApplied()
    } catch (e) {
      setError(e.message)
    } finally {
      setApplying(false)
    }
  }

  const actLabel = activities.find(a => a.key === selectedActivity)?.label ?? selectedActivity

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 600,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 30px 70px rgba(0,0,0,0.3)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ background: '#14532d', padding: '20px 28px', borderRadius: '20px 20px 0 0' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#86efac', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>
            Recomendação de Aplicação
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
            Copiar produtos para múltiplas áreas
          </div>
          <div style={{ fontSize: 12, color: '#86efac', marginTop: 2 }}>
            Defina produtos e doses, selecione as áreas e veja o total necessário
          </div>
        </div>

        <div style={{ overflow: 'auto', padding: '24px 28px', flex: 1 }}>

          {/* Resultado após aplicar */}
          {result && (
            <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: '#14532d', marginBottom: 8 }}>
                ✅ Aplicado em {result.areas_updated} área{result.areas_updated !== 1 ? 's' : ''}!
              </div>
              {Object.entries(result.totals).map(([name, info]) => (
                <div key={name} style={{ fontSize: 13, color: '#166534' }}>
                  <strong>{name}:</strong> {info.total.toFixed(2)} {info.unit}
                </div>
              ))}
              <button onClick={onClose} style={{
                marginTop: 12, background: '#16a34a', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              }}>Fechar</button>
            </div>
          )}

          {/* 1. Selecionar atividade */}
          <div style={{ marginBottom: 20 }}>
            <div style={sectionLabel}>1. Atividade</div>
            <select
              value={selectedActivity}
              onChange={e => setSelectedActivity(e.target.value)}
              style={selectStyle}
            >
              {activities.map(a => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
          </div>

          {/* 2. Produtos e doses */}
          <div style={{ marginBottom: 20 }}>
            <div style={sectionLabel}>2. Produtos e doses</div>

            {/* Tags de produtos */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, minHeight: 40, marginBottom: 12 }}>
              {products.length === 0 && (
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Adicione os produtos abaixo</span>
              )}
              {products.map(p => (
                <span key={p.product_name} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: '#f0fdf4', border: '1.5px solid #86efac',
                  borderRadius: 999, padding: '4px 12px', fontSize: 13, fontWeight: 600, color: '#14532d',
                }}>
                  {p.product_name}
                  {p.dose != null && (
                    <span style={{ fontWeight: 400, color: '#16a34a' }}>&nbsp;{p.dose}{p.unit}/ha</span>
                  )}
                  <button onClick={() => removeProduct(p.product_name)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>

            {/* Input para adicionar */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                ref={nameRef}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProduct())}
                placeholder="Ex: Zapp, Cletodim..."
                style={{ flex: '1 1 140px', ...inputStyle }}
              />
              <input
                type="number"
                value={newDose}
                onChange={e => setNewDose(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProduct())}
                placeholder="Dose"
                min="0"
                step="0.01"
                style={{ width: 80, ...inputStyle }}
              />
              <select
                value={newUnit}
                onChange={e => setNewUnit(e.target.value)}
                style={{ ...inputStyle, padding: '9px 8px', background: '#fff' }}
              >
                {UNITS.map(u => <option key={u} value={u}>{u}/ha</option>)}
              </select>
              <button onClick={addProduct} style={{
                background: '#16a34a', color: '#fff', border: 'none',
                borderRadius: 10, padding: '9px 18px', cursor: 'pointer', fontWeight: 800, fontSize: 18,
              }}>+</button>
            </div>
          </div>

          {/* 3. Selecionar áreas */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={sectionLabel}>3. Áreas ({selectedAreaIds.length} selecionada{selectedAreaIds.length !== 1 ? 's' : ''} — {selectedHa.toFixed(2)} ha)</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAll} style={linkBtn}>Todas</button>
                <button onClick={clearAll} style={linkBtn}>Limpar</button>
              </div>
            </div>

            <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              {areas.map((area, i) => (
                <label key={area.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
                  cursor: 'pointer', background: selectedAreaIds.includes(area.id) ? '#f0fdf4' : (i % 2 === 0 ? '#f8fafc' : '#fff'),
                  borderBottom: i < areas.length - 1 ? '1px solid #e2e8f0' : 'none',
                }}>
                  <input
                    type="checkbox"
                    checked={selectedAreaIds.includes(area.id)}
                    onChange={() => toggleArea(area.id)}
                    style={{ accentColor: '#16a34a', width: 16, height: 16 }}
                  />
                  <span style={{ flex: 1, fontWeight: 600, color: '#14532d', fontSize: 13 }}>{area.name}</span>
                  <span style={{ fontSize: 12, color: '#64748b', fontFamily: "'DM Mono', monospace" }}>{area.ha} ha</span>
                </label>
              ))}
            </div>
          </div>

          {/* Totais em tempo real */}
          {products.length > 0 && selectedAreaIds.length > 0 && (
            <div style={{ background: '#052e16', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#86efac', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
                Total necessário — {selectedAreaIds.length} área{selectedAreaIds.length !== 1 ? 's' : ''} ({selectedHa.toFixed(2)} ha)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {products.map(p => (
                  <div key={p.product_name} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 14px' }}>
                    <div style={{ fontSize: 12, color: '#86efac', fontWeight: 600 }}>{p.product_name}</div>
                    {p.dose != null ? (
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', fontFamily: "'DM Mono', monospace" }}>
                        {(p.dose * selectedHa).toFixed(2)} <span style={{ fontSize: 13 }}>{p.unit}</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: '#64748b' }}>sem dose definida</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 28px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', border: '2px solid #e2e8f0', borderRadius: 10,
            background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#64748b',
          }}>Cancelar</button>
          <button
            onClick={apply}
            disabled={applying || products.length === 0 || selectedAreaIds.length === 0}
            style={{
              flex: 2, padding: '12px', border: 'none', borderRadius: 10,
              background: (products.length === 0 || selectedAreaIds.length === 0) ? '#94a3b8' : '#15803d',
              color: '#fff', cursor: (products.length === 0 || selectedAreaIds.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 700,
            }}
          >
            {applying
              ? 'Aplicando...'
              : `Aplicar a ${selectedAreaIds.length} área${selectedAreaIds.length !== 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8,
}
const inputStyle = {
  padding: '9px 12px', border: '2px solid #e2e8f0', borderRadius: 10,
  fontSize: 13, outline: 'none', fontFamily: 'inherit',
}
const selectStyle = {
  padding: '9px 12px', border: '2px solid #e2e8f0', borderRadius: 10,
  fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff', width: '100%',
}
const linkBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 12, color: '#16a34a', fontWeight: 600, padding: '2px 6px', textDecoration: 'underline',
}
